import { useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Split "Save" control for the mapper top bar.
 *
 *  - Save workbook (primary, ⌘S): keeps the current rules on this sheet with
 *    its edits and AI output. The template is not changed.
 *  - Save template: writes the rules into the bound template (other
 *    workbooks using it get them too); the sheet then follows the template.
 */
export function SaveMenu({
  saving,
  templateName,
  sheetOwnRules,
  onSaveWorkbook,
  onSaveTemplate,
  onSaveAsNewTemplate,
  onBindTemplate,
  onUseTemplateRules,
}: {
  saving: boolean;
  /** Name of the bound template, or null when the sheet has none. */
  templateName: string | null;
  sheetOwnRules: boolean;
  onSaveWorkbook: () => void;
  onSaveTemplate: () => void;
  onSaveAsNewTemplate: () => void;
  onBindTemplate: () => void;
  onUseTemplateRules: () => void;
}) {
  const [open, setOpen] = useState(false);
  const run = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  return (
    <div className="relative flex">
      <button
        type="button"
        onClick={onSaveWorkbook}
        disabled={saving}
        title="Save workbook (⌘S / Ctrl+S): keep these rules, edits and AI output with this workbook"
        className="rounded-l bg-slate-900 px-3 py-1.5 text-[12px] font-medium text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-slate-200"
      >
        {saving ? "Saving…" : "Save"}
      </button>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={saving}
        aria-label="More save options"
        aria-expanded={open}
        className="rounded-r border-l border-white/20 bg-slate-900 px-1.5 py-1.5 text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-950/20 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-slate-200"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default bg-transparent"
            onClick={() => setOpen(false)}
            aria-label="Close save menu"
          />
          <div
            role="menu"
            className="absolute right-0 top-full z-50 mt-1 w-80 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg dark:border-border dark:bg-surface"
          >
            <SaveOption
              title="Save workbook"
              hint="Keeps these rules, your edits and AI output with this workbook. The template is not changed."
              shortcut="⌘S"
              onClick={run(onSaveWorkbook)}
            />
            <SaveOption
              title={templateName ? `Save template “${templateName}”` : "Save template…"}
              hint={
                templateName
                  ? "Updates the template, so other workbooks that use it get these rules too."
                  : "Stores these rules as a reusable template for other workbooks."
              }
              onClick={run(onSaveTemplate)}
            />
            <div className="border-t border-slate-100 dark:border-border" />
            <SaveOption title="Save as new template…" onClick={run(onSaveAsNewTemplate)} />
            <SaveOption title="Bind this sheet to template" onClick={run(onBindTemplate)} />
            {sheetOwnRules && templateName && (
              <SaveOption
                title="Use the template’s rules"
                hint="Drops this workbook’s own rules and follows the template again. Cell edits and AI output stay."
                onClick={run(onUseTemplateRules)}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SaveOption({
  title,
  hint,
  shortcut,
  onClick,
}: {
  title: string;
  hint?: string;
  shortcut?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="block w-full px-3 py-2 text-left transition hover:bg-slate-100 dark:hover:bg-surface-2"
    >
      <span className="flex items-center justify-between gap-2">
        <span className="text-[12.5px] font-medium text-slate-900 dark:text-slate-100">
          {title}
        </span>
        {shortcut && (
          <span className="text-[10.5px] text-slate-400 dark:text-slate-500">{shortcut}</span>
        )}
      </span>
      {hint && (
        <span className="mt-0.5 block text-[11px] leading-snug text-slate-500 dark:text-slate-400">
          {hint}
        </span>
      )}
    </button>
  );
}
